import { AsyncPipe, DOCUMENT, NgFor, NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	NgZone,
	OnDestroy,
	ViewChild
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, Subject, timer } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { stabilizeNotesSectionAfterSend } from '@shared/utils/notes-viewport-focus.util';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { NewMessageIconComponent, SendOutlineIconComponent } from '@itero/ui-toolkit-angular/icons';
import { RxTextareaComponent, RxTextareaSubmitDirective } from '../../components/rx-textarea';
import { NoteCardComponent } from '../../components/note-card/note-card.component';
import { IntuitiveNote } from '../../models/intuitive-note.model';
import { IntuitiveNotesListFacade } from '../../rx-intuitive-notes.facade';

@Component({
	selector: 'rx-intuitive-notes',
	templateUrl: './rx-intuitive-notes.component.html',
	styleUrls: ['./rx-intuitive-notes.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		NgFor,
		AsyncPipe,
		ReactiveFormsModule,
		TranslateModule,
		TlkCardComponent,
		TlkButtonComponent,
		NewMessageIconComponent,
		SendOutlineIconComponent,
		NoteCardComponent,
		RxTextareaComponent,
		RxTextareaSubmitDirective
	],
	providers: [IntuitiveNotesListFacade]
})
export class RxIntuitiveNotesComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	readonly notes$ = this.facade.notes$;

	/** Emits every 30s so note cards re-render and "Just now" / "1 minute ago" updates automatically. */
	readonly relativeTimeRefreshTick$ = timer(0, 30_000).pipe(
		takeUntil(this.destroy$),
		map(() => Date.now())
	);

	readonly newNote = new FormControl<string>('', { nonNullable: false });

	editingNoteId: string | null = null;

	@ViewChild(RxTextareaComponent) private readonly newNoteComposer?: RxTextareaComponent;
	private readonly editingNoteId$ = new BehaviorSubject<string | null>(null);

	readonly createdByName$ = combineLatest([this.shellQuery.user$, this.shellQuery.doctor$]).pipe(
		map(([user, doctor]) => user?.Name ?? doctor?.Name ?? null)
	);

	readonly canDelete$ = this.shellQuery.getReadOnlyState('notes').pipe(map(readOnly => !readOnly));

	/** Notes list + delete permission for a single `async` in the template. */
	readonly notesPanelVm$ = combineLatest([this.notes$, this.canDelete$]).pipe(
		map(([notes, canDelete]) => ({ notes, canDelete }))
	);

	/** Composer row: editable shell and no card in edit mode. */
	readonly showAddComposer$ = combineLatest([this.canDelete$, this.editingNoteId$]).pipe(
		map(([canDelete, editingId]) => !editingId && canDelete)
	);

	readonly dateFormat$ = this.shellQuery.dateFormat$;

	constructor(
		private readonly facade: IntuitiveNotesListFacade,
		private readonly shellQuery: ShellQuery,
		private readonly cdr: ChangeDetectorRef,
		private readonly hostPlatformService: HostPlatformService,
		private readonly ngZone: NgZone,
		@Inject(DOCUMENT) private readonly document: Document
	) {
		this.editingNoteId$.next(this.editingNoteId);
	}

	get isSendNoteButtonDisabled(): boolean {
		return !this.newNote.value?.trim();
	}

	private setEditingNoteId(id: string | null): void {
		this.editingNoteId = id;
		this.editingNoteId$.next(id);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onNewNoteSubmitted(value: string | null): void {
		const content = value?.trim();

		if (!content) {
			return;
		}

		this.newNote.reset('');
		this.createdByName$.pipe(take(1)).subscribe(createdByName => {
			this.facade.addNote(content, createdByName);
			this.cdr.detectChanges();
			this.stabilizeNotesViewportAfterSend();
		});
	}

	/** Issue 2 / TR-90903 — see `stabilizeNotesSectionAfterSend`. */
	private stabilizeNotesViewportAfterSend(): void {
		stabilizeNotesSectionAfterSend(
			this.document,
			this.ngZone,
			this.destroy$,
			() => this.newNoteComposer?.focusInput(),
			this.hostPlatformService.isScanner
		);
	}

	handleEdit(id: string, content: string): void {
		this.facade.editNote(id, content);
		this.setEditingNoteId(null);
	}

	handleEditModeChange(noteId: string, isEditing: boolean): void {
		this.setEditingNoteId(isEditing ? noteId : null);
	}

	handleRemove(id: string): void {
		this.facade.removeNote(id);
		if (this.editingNoteId === id) {
			this.setEditingNoteId(null);
		}
	}

	trackByNote(index: number, note: IntuitiveNote): string {
		return note.id;
	}
}
